import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { FileText, AlertCircle, Loader2, CheckCircle2, Mail, FileSignature, Package, BookMarked } from 'lucide-react';
import {
  generateProjectReport,
  getProjectReportVersions,
  clearGenerateProjectReport,
  fetchARKRegistryDocs,
} from '../../../../../redux/ProductionData/actions';
import { cn } from '../../../../../lib/utils';

interface GenerateProjectReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | string;
  projectName: string;
  jobNumber?: string;
  approvedProductionReports?: any[];
  approvedMaterialReports?: any[];
  approvedIRNReports?: any[];
  approvedEquipmentReports?: any[];
  approvedQCReports?: any[];
  approvedConsumableReports?: any[];
  approvedMiscellaneousReports?: any[];
}

export const GenerateProjectReportModal: React.FC<GenerateProjectReportModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  jobNumber,
  approvedProductionReports = [],
  approvedMaterialReports = [],
  approvedIRNReports = [],
  approvedEquipmentReports = [],
  approvedQCReports = [],
  approvedConsumableReports = [],
  approvedMiscellaneousReports = []
}) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<string[]>([]);

  // Get project report state from Redux
  const {
    generateProjectReportLoading,
    generateProjectReportSuccess,
    generateProjectReportError,
    projectReportVersionsLoading,
    projectReportVersions,
    projectReportVersionsError,
    arkRegistryDocs,
  } = useSelector((state: any) => state.productionData || {});

  // Same document list shape as ARKRegistryDocumentTable
  const arkDocuments = useMemo(() => {
    if (Array.isArray(arkRegistryDocs)) return arkRegistryDocs;
    if (arkRegistryDocs && typeof arkRegistryDocs === 'object' && arkRegistryDocs.documents) {
      return Array.isArray(arkRegistryDocs.documents) ? arkRegistryDocs.documents : [];
    }
    return [];
  }, [arkRegistryDocs]);

  // Only types that exist on uploaded docs (d.type) — not the full tags catalog
  const documentTypes = useMemo(() => {
    const unique = new Set<string>();
    arkDocuments.forEach((d: any) => {
      const t = String(d?.type ?? '').trim();
      if (t) unique.add(t);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [arkDocuments]);

  // Fetch ARK registry docs + report versions when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      dispatch(fetchARKRegistryDocs({ projectId }));
      dispatch(getProjectReportVersions(projectId));
    } else if (!isOpen) {
      setEmail('');
      setEmailError('');
      setSelectedDocumentTypes([]);
      dispatch(clearGenerateProjectReport());
    }
  }, [isOpen, projectId, dispatch]);

  // Default-select only types present on project documents
  useEffect(() => {
    if (isOpen) {
      setSelectedDocumentTypes(documentTypes);
    }
  }, [isOpen, documentTypes]);

  // Clear success message after showing email confirmation
  useEffect(() => {
    if (generateProjectReportSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearGenerateProjectReport());
        onClose();
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [generateProjectReportSuccess, dispatch, onClose]);

  // Validate email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Handle generate report
  const handleGenerateReport = () => {
    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (documentTypes.length > 0 && selectedDocumentTypes.length === 0) {
      return;
    }

    const payload: any = {
      projectId,
      emailAddress: [email],
      documentTypes: selectedDocumentTypes,
    };

    dispatch(generateProjectReport(payload));
  };

  // Process project report versions
  const processedVersions = useMemo(() => {
    if (!projectReportVersions || !Array.isArray(projectReportVersions)) {
      return [];
    }
    
    return [...projectReportVersions].sort((a: any, b: any) => {
      const refA = a.docRef || a.versionNumber || 0;
      const refB = b.docRef || b.versionNumber || 0;
      return refB - refA;
    });
  }, [projectReportVersions]);

  // Calculate summary
  const summary = useMemo(() => {
    return {
      production: approvedProductionReports.length,
      material: approvedMaterialReports.length,
      irn: approvedIRNReports.length,
      equipment: approvedEquipmentReports.length,
      qc: approvedQCReports.length,
      consumable: approvedConsumableReports.length,
      miscellaneous: approvedMiscellaneousReports.length,
      total: approvedProductionReports.length + approvedMaterialReports.length + 
             approvedIRNReports.length + approvedEquipmentReports.length + 
             approvedQCReports.length + approvedConsumableReports.length +
             approvedMiscellaneousReports.length
    };
  }, [approvedProductionReports, approvedMaterialReports, approvedIRNReports, approvedEquipmentReports, approvedQCReports, approvedConsumableReports, approvedMiscellaneousReports]);

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SUMMARY_TILES = [
    { key: 'production', label: 'Production', value: summary.production, icon: FileSignature },
    { key: 'material', label: 'Material', value: summary.material, icon: Package },
    { key: 'irn', label: 'IRN', value: summary.irn, icon: FileSignature },
    { key: 'equipment', label: 'Equipment', value: summary.equipment, icon: BookMarked },
    { key: 'consumable', label: 'Consumable', value: summary.consumable, icon: FileSignature },
    { key: 'miscellaneous', label: 'Miscellaneous', value: summary.miscellaneous, icon: FileSignature },
    { key: 'qc', label: 'QC', value: summary.qc, icon: FileSignature },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[1100px] w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-page-bg">
        <DialogHeader className="flex-shrink-0 pl-4 sm:pl-6 pr-14 py-4 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between gap-4">
          <div className="min-w-0">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Generate Project Report
            </DialogTitle>
            <p className="text-[12px] text-heritage-grey mt-1 truncate">
              {projectName && `Project: ${projectName}`}
              {jobNumber && <span className="ml-2">· Job: {jobNumber}</span>}
            </p>
          </div>
          <Button
            onClick={handleGenerateReport}
            disabled={
              generateProjectReportLoading ||
              !email ||
              !!emailError ||
              summary.total === 0 ||
              (documentTypes.length > 0 && selectedDocumentTypes.length === 0)
            }
            className="bg-hero-red hover:bg-hero-red-60 text-white flex-shrink-0"
          >
            {generateProjectReportLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Generate &amp; Merge
              </>
            )}
          </Button>
        </DialogHeader>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          {/* Summary tiles */}
          <div className="bg-white rounded-xl border border-[#E2E5EA]">
            <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center justify-between">
              <h3 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider">Report Summary</h3>
              <span className="text-[11px] text-heritage-grey">
                Total approved: <span className="font-semibold text-blueprint-blue">{summary.total}</span>
              </span>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {SUMMARY_TILES.map(({ key, label, value, icon: Icon }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                  <div className="w-[30px] h-[30px] rounded-md bg-[rgba(0,39,63,0.06)] flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-blueprint-blue" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-[18px] font-bold text-blueprint-blue leading-none">{value}</div>
                    <div className="text-[10px] font-semibold text-heritage-grey uppercase tracking-wider mt-0.5">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Document Types — only types present on uploaded ARK registry docs */}
          {documentTypes.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E2E5EA]">
              <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA]">
                <h3 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider">Document Types</h3>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {documentTypes.map((docTypeName) => {
                  const isChecked = selectedDocumentTypes.includes(docTypeName);

                  return (
                    <button
                      key={docTypeName}
                      type="button"
                      onClick={() => {
                        if (isChecked) {
                          setSelectedDocumentTypes(selectedDocumentTypes.filter((n) => n !== docTypeName));
                        } else {
                          setSelectedDocumentTypes([...selectedDocumentTypes, docTypeName]);
                        }
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors",
                        isChecked
                          ? "bg-hero-red text-white hover:bg-hero-red-60"
                          : "bg-white text-text-grey border border-[#E2E5EA] hover:bg-page-bg"
                      )}
                    >
                      {isChecked && <CheckCircle2 className="h-3 w-3" />}
                      {docTypeName}
                    </button>
                  );
                })}
              </div>
              {selectedDocumentTypes.length === 0 && (
                <div className="px-4 pb-3 text-[12px] text-[#B38600]">
                  Select at least one document type.
                </div>
              )}
            </div>
          )}

          {/* Email Notification */}
          <div className="bg-white rounded-xl border border-[#E2E5EA]">
            <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA]">
              <h3 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider">Email Notification</h3>
            </div>
            <div className="p-4 space-y-2">
              <label htmlFor="email" className="text-[12px] font-medium text-text-grey">
                Email Address <span className="text-hero-red">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-heritage-grey" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={cn("pl-10", emailError && "border-hero-red focus:ring-hero-red")}
                />
              </div>
              {emailError && (
                <p className="text-[12px] text-hero-red flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {emailError}
                </p>
              )}
              <p className="text-[11px] text-heritage-grey">
                The merged project report will be sent to this email address.
              </p>
            </div>
          </div>

          {/* Inline status banners */}
          {generateProjectReportSuccess && (
            <div className="flex items-center gap-2 p-3 bg-[rgba(31,154,58,0.06)] border border-[rgba(31,154,58,0.2)] rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-galvanizing-green" />
              <span className="text-[13px] text-galvanizing-green">
                Report generation started. The merged PDF will be emailed to {email || 'your inbox'} when ready.
              </span>
            </div>
          )}
          {generateProjectReportError && (
            <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <AlertCircle className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{generateProjectReportError}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

